import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { AuthShell, Field } from "@/components/AuthShell";

export default function Login() {
  const { user, loading, login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  if (!loading && user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const ch = await login(email, password);
      if (ch) setChallenge(ch);
      else navigate("/app");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await verify2fa(challenge, code);
      navigate("/app");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (challenge) {
    return (
      <AuthShell kicker="ESPACE TECHNICIEN" title="Vérification en deux étapes">
        <form onSubmit={submitCode} className="space-y-4" data-testid="twofa-form">
          <p className="text-sm text-slate-400">Saisissez le code à 6 chiffres affiché dans votre application d'authentification.</p>
          <Field label="Code de vérification">
            <Input data-testid="twofa-code-input" inputMode="numeric" autoFocus maxLength={9} value={code}
              onChange={(e) => setCode(e.target.value)} className="text-center text-2xl tracking-[0.3em] font-mono" />
          </Field>
          {error && <p data-testid="twofa-error" className="text-sm text-red-400">{error}</p>}
          <Button data-testid="twofa-submit-btn" type="submit" disabled={busy} className="w-full rounded-full font-semibold">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Valider
          </Button>
          <button type="button" onClick={() => { setChallenge(null); setCode(""); setError(""); }} className="text-sm text-slate-400 hover:underline" data-testid="twofa-back-btn">
            Revenir à la connexion
          </button>
          <p className="text-xs text-slate-500">Vous pouvez aussi saisir un de vos codes de secours.</p>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell kicker="ESPACE TECHNICIEN" title="Connexion">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="E-mail">
          <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Mot de passe">
          <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
        <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full rounded-full font-semibold">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Se connecter
        </Button>
      </form>
      <p className="text-sm text-slate-400 mt-8">
        Pas encore de compte ? <Link to="/register" className="text-sky-400 hover:underline" data-testid="go-register-link">Créer mon espace</Link>
      </p>
    </AuthShell>
  );
}
